import type { PropsWithChildren } from 'react'

import { type BudgetCategoryTypeOption } from '@webapp/components/Budgets/BudgetCategoryTypeSelect/BudgetCategoryTypeSelect'
import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { SavedFilterManager } from '@webapp/components/SavedFilterManager'
import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { type IBudgetCategoryListViewFilters } from '@webapp/hooks/useBudgetCategoryListFilterManager'
import {
  getCustomFieldFilterKey,
  toCustomFieldFilter,
} from '@webapp/hooks/useBudgetCategoryListFilterManager/utils'
import { type SavedFilterCollectionKeys } from '@webapp/hooks/useSavedFilterManager/types'
import {
  formatToAccountCodeFilter,
  formatToDepartmentWithBranchFilter,
  formatToUserFilter,
} from '@webapp/hooks/useSavedFilterManager/utils'
import { type IBudgetCustomFieldConditionValue } from '@webapp/platform/budgets'
import { getFullName } from '@webapp/utils/user'

export interface IBudgetCategoryListSavedFilterManagerProps {
  collectionId: keyof Pick<
    SavedFilterCollectionKeys,
    'settings.manage.budget.category' | 'dashboard.budget.category.list'
  >
  filterManager: FilterManagerContextValues<IBudgetCategoryListViewFilters>
  budgetCategoryTypeOptions: BudgetCategoryTypeOption[]
  /**
   * The custom fields whose filters can be saved. Each one gets a
   * `custom_field_<id>` entry, so a saved field that is gone has no decoder,
   * and applying the saved filter skips it.
   */
  savableCustomFields?: CustomField[]
}

export const BudgetCategoryListSavedFilterManager = ({
  collectionId,
  filterManager,
  budgetCategoryTypeOptions,
  savableCustomFields = [],
  children,
}: PropsWithChildren<IBudgetCategoryListSavedFilterManagerProps>) => {
  const allowedFilters: (keyof IBudgetCategoryListViewFilters)[] = [
    'departments',
    'account_codes',
  ]

  if (collectionId === 'settings.manage.budget.category')
    allowedFilters.push('users', 'source_of_truth')

  allowedFilters.push(
    ...savableCustomFields.map((customField) =>
      getCustomFieldFilterKey(customField.id)
    )
  )

  const decodeFilters = {
    departments: (data) =>
      formatToDepartmentWithBranchFilter('departments', data),
    account_codes: (data) => formatToAccountCodeFilter('account_codes', data),
    users: (data) => formatToUserFilter('users', data),
    source_of_truth: (data) => ({
      key: 'source_of_truth',
      paramValue: data,
      value: budgetCategoryTypeOptions.find((option) => option.value === data),
    }),
    ...savableCustomFields.reduce(
      (customFieldDecoders, customField) => ({
        ...customFieldDecoders,
        [getCustomFieldFilterKey(customField.id)]: (savedValues: string[]) =>
          toCustomFieldFilter({ field: customField, values: savedValues }),
      }),
      {}
    ),
  }

  const encodeFilters = {
    departments: (value) => ({
      id: value.id,
      name: value.name,
      branch: value.branch,
    }),
    account_codes: (value) => ({
      id: value.id,
      code: `${value.code}`,
      description: value.description,
    }),
    users: (value) => ({
      id: value.id,
      name: getFullName(value),
      _metadata: {
        firstName: value.firstName,
        lastName: value.lastName,
      },
    }),
    source_of_truth: (value) => (value.value === 'True' ? 'True' : 'False'), // explict check for 'True' b/c source_of_truth can also be an empty string
    ...savableCustomFields.reduce(
      (customFieldEncoders, customField) => ({
        ...customFieldEncoders,
        [getCustomFieldFilterKey(customField.id)]: (
          value: IBudgetCustomFieldConditionValue
        ) => value.values,
      }),
      {}
    ),
  }

  return (
    <SavedFilterManager
      collectionId={collectionId}
      filterManager={filterManager}
      allowedFilters={allowedFilters}
      decodeFilters={decodeFilters}
      encodeFilters={encodeFilters}
    >
      {children}
    </SavedFilterManager>
  )
}

BudgetCategoryListSavedFilterManager.displayName =
  'BudgetCategoryListSavedFilterManager'
